"use client";

import { useEffect, useState } from "react";
import type { ExplorePage, ExploreQuery } from "@/lib/explore";

export function useDiscoveries(filters: ExploreQuery, initialPage: ExplorePage) {
  const [retry, setRetry] = useState(0);
  const isInitial = !filters.query && filters.category === "All" && !filters.savedOnly && filters.sort === "curated" && filters.page === 0;
  const body = JSON.stringify({ ...filters, saved: filters.savedOnly ? filters.saved : [] });
  const key = `${body}:${retry}`;
  const [response, setResponse] = useState<{ key: string; data: ExplorePage; error?: string } | null>(null);
  useEffect(() => {
    if (isInitial) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      fetch("/api/discoveries", { method: "POST", headers: { "Content-Type": "application/json" }, body, signal: controller.signal })
        .then(async (result) => { if (!result.ok) throw new Error("Search unavailable"); return result.json() as Promise<ExplorePage>; })
        .then((data) => { if (!controller.signal.aborted) setResponse({ key, data }); })
        .catch(() => { if (!controller.signal.aborted) setResponse({ key, data: initialPage, error: "The collection couldn’t load. Please try again." }); });
    }, filters.query ? 180 : 0);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [body, key, isInitial, initialPage, filters.query]);
  return {
    data: isInitial ? initialPage : response?.data ?? initialPage,
    loading: !isInitial && response?.key !== key,
    error: !isInitial && response?.key === key ? response.error : undefined,
    retry: () => setRetry((value) => value + 1),
  };
}
